/**
 * Workspace dashboard — landing per cliente.
 *
 * Cuando entrás a /c/:clientSlug ves esta página: hero del cliente y un
 * grid de "opciones" (Inbox, Publicaciones, Métricas, Alertas, Cuentas,
 * Skills) cada una con su contador correspondiente.
 *
 * Inspirado en el patrón del social.champe viejo donde cada workspace
 * exponía sus secciones en cards al entrar.
 */
import { Link, useParams } from "@tanstack/react-router";
import {
  Inbox,
  Calendar,
  BarChart3,
  Bell,
  Users,
  Brain,
  ArrowLeft,
  ArrowUpRight,
  MessageSquare,
} from "lucide-react";
import { findClient } from "@/lib/clients";
import { useConversations } from "@/hooks/use-conversations";
import { useAccounts, type AccountWithToggle } from "@/hooks/use-accounts";
import { StatusDot } from "@/components/layout/status-dot";
import { cn } from "@/lib/cn";
import type { ClientSlug } from "@/types";

const PLATFORM_LABEL: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
};

const PLATFORM_SHORT: Record<string, string> = {
  instagram: "IG",
  facebook: "FB",
  whatsapp: "WA",
};

type AccountStatus = "active" | "paused" | "kill";

function accountStatus(acc: AccountWithToggle): AccountStatus {
  const t = acc.toggle;
  if (t && t.killSwitchLevel !== "none") return "kill";
  if (t && t.moderationEnabled) return "active";
  return "paused";
}

export function WorkspacePage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug?: string };
  const client = findClient(clientSlug);
  const { data: conversations } = useConversations(
    client ? { client: client.slug } : undefined,
  );
  const { data: accounts } = useAccounts(client?.slug);

  if (!client) {
    return (
      <div className="min-h-full bg-background flex items-center justify-center p-7">
        <div className="card-surface p-8 max-w-md text-center">
          <h1 className="font-sans text-xl font-semibold text-text-primary mb-2">
            Workspace no encontrado
          </h1>
          <p className="font-sans text-sm text-text-secondary mb-4">
            El cliente <code className="font-mono">{clientSlug}</code> no existe o
            no tenés acceso.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-sans text-sm text-magenta hover:underline"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const list = conversations ?? [];
  const activeCount = list.filter(
    (c) => c.state === "bot" || c.state === "human" || c.state === "escalated",
  ).length;
  const escalatedCount = list.filter((c) => c.state === "escalated").length;
  const overdueCount = list.filter((c) => c.slaRemainingSec < 0).length;

  const statusVariant =
    client.health === "ok" ? "success" : client.health === "warn" ? "warning" : "muted";

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-5xl mx-auto px-7 py-8">
        {/* Breadcrumb */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 font-sans text-xs text-text-muted hover:text-text-primary mb-5"
        >
          <ArrowLeft size={12} strokeWidth={1.75} />
          Todos los clientes
        </Link>

        {/* Hero */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <StatusDot variant={statusVariant} size={8} />
            <span className="font-sans text-2xs font-medium text-text-muted">
              {client.market}
            </span>
          </div>
          <h1 className="font-sans text-4xl font-semibold tracking-tight text-text-primary">
            {client.fullName}
          </h1>
          <p className="font-sans text-md text-text-secondary mt-2">
            {client.tagline}
          </p>

          {/* Plataformas */}
          <div className="flex items-center gap-1.5 mt-4">
            {client.platforms.map((p) => (
              <span
                key={p}
                className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2.5 py-0.5 text-xs font-sans text-text-secondary"
              >
                {PLATFORM_LABEL[p] ?? p}
              </span>
            ))}
          </div>
        </header>

        {/* Grid de opciones */}
        <section aria-label="Secciones del workspace">
          <h2 className="section-label mb-3">Secciones</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <OptionCard
              to="/c/$clientSlug/inbox"
              params={{ clientSlug: client.slug }}
              icon={<Inbox size={18} strokeWidth={1.5} />}
              title="Inbox"
              description="DMs + comentarios cross-canal"
              counter={activeCount}
              counterTone={escalatedCount > 0 ? "magenta" : "default"}
              counterLabel={
                escalatedCount > 0
                  ? `${escalatedCount} escaladas`
                  : "activas"
              }
            />
            <OptionCard
              to="/c/$clientSlug/publicaciones"
              params={{ clientSlug: client.slug }}
              icon={<Calendar size={18} strokeWidth={1.5} />}
              title="Publicaciones"
              description="Cronograma vía Zernio"
              counterLabel="próximamente"
              disabled
            />
            <OptionCard
              to="/c/$clientSlug/metricas"
              params={{ clientSlug: client.slug }}
              icon={<BarChart3 size={18} strokeWidth={1.5} />}
              title="Métricas"
              description="KPIs de moderación y respuesta"
              counterLabel="próximamente"
              disabled
            />
            <OptionCard
              to="/c/$clientSlug/alertas"
              params={{ clientSlug: client.slug }}
              icon={<Bell size={18} strokeWidth={1.5} />}
              title="Alertas"
              description="Spikes, SLA, fallos del bot"
              counter={overdueCount}
              counterTone={overdueCount > 0 ? "warning" : "default"}
              counterLabel={overdueCount > 0 ? "SLA vencidos" : "sin alertas"}
            />
            <AccountsCard
              clientSlug={client.slug as ClientSlug}
              accounts={accounts ?? []}
              loading={!accounts}
            />
            <OptionCard
              to="/c/$clientSlug/skills"
              params={{ clientSlug: client.slug }}
              icon={<Brain size={18} strokeWidth={1.5} />}
              title="Manuales"
              description="Editor de manuales de moderación con historial y diff visual"
              counterLabel="versionados"
            />
            <OptionCard
              to="/c/$clientSlug/simulador"
              params={{ clientSlug: client.slug }}
              icon={<MessageSquare size={18} strokeWidth={1.5} />}
              title="Simulador"
              description="Chat de prueba — validá lo bueno, corregí lo malo"
              counterLabel="entrenamiento del bot"
            />
          </div>
        </section>
      </div>
    </div>
  );
}

interface OptionCardProps {
  to: string;
  params: Record<string, string>;
  icon: React.ReactNode;
  title: string;
  description: string;
  counter?: number;
  counterLabel: string;
  counterTone?: "default" | "magenta" | "warning";
  /** Si true, la card se ve atenuada y no es clickeable. Para secciones
   *  pendientes de desarrollo (publicaciones, métricas). */
  disabled?: boolean;
}

function OptionCard({
  to,
  params,
  icon,
  title,
  description,
  counter,
  counterLabel,
  counterTone = "default",
  disabled,
}: OptionCardProps) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            "transition-colors",
            disabled ? "text-text-disabled" : "text-text-muted group-hover:text-magenta",
          )}
        >
          {icon}
        </div>
        {disabled ? (
          <span className="font-mono text-2xs uppercase tracking-wider text-text-disabled border border-default rounded-full px-2 py-0.5">
            próximamente
          </span>
        ) : (
          <ArrowUpRight
            size={14}
            strokeWidth={1.5}
            className="text-text-disabled group-hover:text-magenta transition-colors"
          />
        )}
      </div>
      <h3
        className={cn(
          "font-sans text-md font-semibold mt-3",
          disabled ? "text-text-secondary" : "text-text-primary",
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          "font-sans text-xs mt-1",
          disabled ? "text-text-muted" : "text-text-secondary",
        )}
      >
        {description}
      </p>

      <div className="mt-3 flex items-baseline gap-1.5">
        {!disabled && counter !== undefined && (
          <span
            className={cn(
              "font-mono text-base tabular-nums",
              counterTone === "magenta" && "text-magenta",
              counterTone === "warning" && "text-warning-text",
              counterTone === "default" && "text-text-primary",
            )}
          >
            {counter}
          </span>
        )}
        <span className="font-sans text-xs text-text-muted">{counterLabel}</span>
      </div>
    </>
  );

  if (disabled) {
    return (
      <div
        aria-disabled="true"
        title="Sección no activa todavía"
        className={cn(
          "card-surface p-5 block opacity-50 cursor-not-allowed",
          "border-dashed",
        )}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={to}
      params={params as never}
      className={cn(
        "card-surface p-5 block group",
        "hover:shadow-md hover:border-strong transition-all",
        "focus:outline-none focus:shadow-focus",
      )}
    >
      {inner}
    </Link>
  );
}

/**
 * Card especial para "Cuentas" — además de linkear a /cuentas muestra el
 * estado de moderación por plataforma con dots de color y un mini-toggle
 * "Pausar todo / Reanudar todo" para acción de un click sin entrar a la
 * página de detalle.
 *
 * Estado por cuenta:
 *  - active  → moderación on, sin freno
 *  - paused  → moderación off (operador la pausó)
 *  - kill    → freno de emergencia activo (cualquier nivel)
 */
function AccountsCard({
  clientSlug,
  accounts,
  loading,
}: {
  clientSlug: ClientSlug;
  accounts: AccountWithToggle[];
  loading: boolean;
}) {
  const killCount = accounts.filter((a) => accountStatus(a) === "kill").length;
  const total = accounts.length;

  // Card vacía (sin cuentas conectadas).
  if (!loading && total === 0) {
    return (
      <Link
        to="/c/$clientSlug/cuentas"
        params={{ clientSlug } as never}
        className={cn(
          "card-surface p-5 block group",
          "hover:shadow-md hover:border-strong transition-all",
          "focus:outline-none focus:shadow-focus",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="text-text-muted group-hover:text-magenta transition-colors">
            <Users size={18} strokeWidth={1.5} />
          </div>
          <ArrowUpRight
            size={14}
            strokeWidth={1.5}
            className="text-text-disabled group-hover:text-magenta transition-colors"
          />
        </div>
        <h3 className="font-sans text-md font-semibold mt-3 text-text-primary">
          Cuentas
        </h3>
        <p className="font-sans text-xs mt-1 text-text-secondary">
          Conectá IG, FB o WhatsApp para arrancar.
        </p>
        <div className="mt-3 flex items-baseline gap-1.5">
          <span className="font-mono text-base tabular-nums text-text-primary">
            0
          </span>
          <span className="font-sans text-xs text-text-muted">conectadas</span>
        </div>
      </Link>
    );
  }

  // Toda la card es UN solo target clickable → cero riesgo de misclick.
  // Los toggles que mostramos son indicadores de estado, NO controles.
  // Para editar, el usuario entra a /cuentas (siguiente Link).
  return (
    <Link
      to="/c/$clientSlug/cuentas"
      params={{ clientSlug } as never}
      aria-label="Ir a Cuentas"
      className={cn(
        "card-surface p-5 block group",
        "hover:shadow-md hover:border-strong transition-all",
        "focus:outline-none focus:shadow-focus",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-text-muted group-hover:text-magenta transition-colors">
          <Users size={18} strokeWidth={1.5} />
        </div>
        <ArrowUpRight
          size={14}
          strokeWidth={1.5}
          className="text-text-disabled group-hover:text-magenta transition-colors"
        />
      </div>
      <h3 className="font-sans text-md font-semibold mt-3 text-text-primary">
        Cuentas
      </h3>
      <p className="font-sans text-xs mt-1 text-text-secondary">
        IG/FB/WhatsApp y kill switches
      </p>

      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-mono text-base tabular-nums text-text-primary">
          {loading ? "—" : total}
        </span>
        <span className="font-sans text-xs text-text-muted">
          {loading
            ? "cargando…"
            : killCount > 0
              ? `conectadas · ${killCount} con freno`
              : "conectadas"}
        </span>
      </div>

      {/* Filas por cuenta — solo lectura, click va a /cuentas (el Link padre) */}
      {!loading && accounts.length > 0 && (
        <div className="mt-3 space-y-2">
          {accounts.map((acc) => (
            <AccountStateRow key={acc.id} account={acc} />
          ))}
          <p className="font-sans text-2xs text-text-muted pt-1">
            Tocá la card para editar.
          </p>
        </div>
      )}
    </Link>
  );
}

/**
 * Fila visual de una cuenta dentro de la card de Cuentas en la landing.
 * Solo lectura — muestra el estado de los toggles pero no responde al click.
 * El click llega al Link padre, que navega a /cuentas.
 */
function AccountStateRow({ account }: { account: AccountWithToggle }) {
  const status = accountStatus(account);
  const dotColor =
    status === "kill"
      ? "var(--error)"
      : status === "active"
        ? "var(--success)"
        : "var(--text-disabled)";
  const dotTitle =
    status === "kill"
      ? "Freno de emergencia activo"
      : status === "active"
        ? "Operando normal"
        : "Cuenta pausada";
  const moderationOn = account.toggle?.moderationEnabled ?? false;
  const publishingOn = account.toggle?.publishingEnabled ?? false;
  const platformLabel = PLATFORM_SHORT[account.platform] ?? account.platform.slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-surface-2 px-2.5 py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="inline-block w-2 h-2 rounded-full ring-1 ring-black/30 shrink-0"
          style={{ background: dotColor }}
          title={dotTitle}
        />
        <span className="font-sans text-2xs font-semibold text-text-primary">
          {platformLabel}
        </span>
        {account.handle && (
          <span className="font-sans text-2xs text-text-muted truncate">
            @{account.handle}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <ToggleIndicator label="Moderación" on={moderationOn} />
        <ToggleIndicator label="Publicación" on={publishingOn} />
      </div>
    </div>
  );
}

/**
 * Switch visual NO interactivo. Mismo estilo que el switch real de /cuentas
 * para que el usuario reconozca el patrón, pero sin onClick. El click
 * burbujea al Link padre y navega a /cuentas.
 */
function ToggleIndicator({ label, on }: { label: string; on: boolean }) {
  return (
    <div
      className="inline-flex items-center gap-1.5"
      title={`${label}: ${on ? "activa" : "apagada"}`}
    >
      <span className="font-sans text-2xs text-text-muted">{label}</span>
      <span
        className={cn(
          "relative inline-flex shrink-0 h-3.5 w-7 items-center rounded-full",
          on ? "bg-magenta" : "bg-surface-3",
        )}
        aria-hidden="true"
      >
        <span
          className={cn(
            "inline-block w-2.5 h-2.5 rounded-full bg-white transition-transform",
            on ? "translate-x-3.5" : "translate-x-0.5",
          )}
        />
      </span>
    </div>
  );
}
